import { INotificationMonitoringItem, TNotificationAction } from '../../interfaces/INotificationMonitoring';

// Code set by the backend auth guard (control_and_get_context_user)
const FORBIDDEN_CODE = 'FORBIDDEN';

// AppError wraps the original error
const unwrap = (error: unknown): unknown =>
	error && typeof error === 'object' && 'error' in error ? unwrap((error as { error: unknown }).error) : error;

const errorMessage = (error: unknown): string => {
	const original = unwrap(error);
	if (original instanceof Error) {
		return original.message;
	}
	return String(original ?? '');
};

const isAccessDenied = (error: unknown): boolean => {
	// `graphql-request` throws a `ClientError` whose shape isn't exported,
	// so we narrow with a structural cast instead of an instanceof check.
	const gqlError = unwrap(error) as { response?: { errors?: { extensions?: { code?: string } }[] } } | null;
	return (gqlError?.response?.errors ?? []).some((e) => e.extensions?.code === FORBIDDEN_CODE);
};

type TActionTarget = Pick<INotificationMonitoringItem, 'statusTag' | 'sendDatetime' | 'retryable'>;

// "Send now" on an error Mandrill never answered (backend `retryable`, e.g. an
// interrupted cron run): the mail may already have left
const isRetry = (action: TNotificationAction, item: TActionTarget): boolean => action === 'send' && item.retryable;

// Which actions make sense for a notification in a given status
const availableActions = (item: TActionTarget): TNotificationAction[] => {
	const actions: TNotificationAction[] = [];
	if (item.statusTag === 'planned' || item.retryable) {
		actions.push('send');
	}
	if (item.statusTag !== 'planned' && item.sendDatetime) {
		actions.push('resynchronize');
	}
	actions.push('delete');
	return actions;
};

const HAdminNotificationMonitoring = { errorMessage, isAccessDenied, isRetry, availableActions };

export default HAdminNotificationMonitoring;
